import { type QueryStatus } from '@tanstack/react-query';
import { useMemo } from 'react';

import { useMy } from '@/hooks/useMy';
import {
  computeScore,
  emptyCounts,
  extractSeverityFromStatus,
  SeverityCounts,
} from '@/sections/customerManagement/utils/leaderboardScoring';
import { GraphQuery, Risk, RiskSeverity } from '@/types';

export interface LeaderboardEntry {
  rank: number;
  email: string;
  displayName: string;
  score: number;
  counts: SeverityCounts;
}

const leaderboardQuery: GraphQuery = {
  node: {
    labels: ['Risk'],
    filters: [
      {
        field: '',
        operator: 'OR',
        value: [
          { field: 'status', operator: 'STARTS WITH', value: 'O' },
          { field: 'status', operator: 'STARTS WITH', value: 'R' },
          { field: 'status', operator: 'STARTS WITH', value: 'I' },
        ],
      },
      { field: 'source', operator: 'ENDS WITH', value: '@praetorian.com' },
    ],
  },
  limit: 10000,
};

const EXCLUDED_EMAILS = new Set([
  'anthony.paimany@praetorian.com',
  'ayden.colby@praetorian.com',
  'bruce.kang@praetorian.com',
  'ryan.syed@praetorian.com',
  'dhiral.vyas@praetorian.com',
  'msp@praetorian.com',
  'ayden.colby+cushman2@praetorian.com',
  'managed_services@praetorian.com',
  'ayman.hammad@praetorian.com',
  'francisco.rosales@praetorian.com',
  'ben.kofman@praetorian.com',
  'peter.mueller@praetorian.com',
  'dan.crawford@praetorian.com',
  'ismaeel.mian@praetorian.com',
  'connor.holm@praetorian.com',
  'walter.sagehorn@praetorian.com',
]);

type CountsSeverity = keyof SeverityCounts;

function isCountsSeverity(s: RiskSeverity): s is CountsSeverity {
  return (
    s === RiskSeverity.Critical ||
    s === RiskSeverity.High ||
    s === RiskSeverity.Medium ||
    s === RiskSeverity.Low ||
    s === RiskSeverity.Info
  );
}

export function useLeaderboardData(): {
  entries: LeaderboardEntry[];
  status: QueryStatus;
} {
  const query = useMy(
    {
      resource: 'risk',
      query: leaderboardQuery,
      allTenants: true,
    },
    {
      doNotImpersonate: true,
    }
  );

  const entries = useMemo(() => {
    const risks = (query.data ?? []) as Risk[];

    // Track unique vulnerability names per analyst per severity to avoid
    // counting the same finding on multiple hosts more than once
    const seenNames = new Map<string, Set<string>>();
    const byEmail = new Map<string, SeverityCounts>();

    for (const risk of risks) {
      const email = risk.source;
      if (!email) continue;

      // Exclude service/tenant accounts (chariot+customer@praetorian.com)
      if (email.startsWith('chariot+')) continue;

      // Exclude specific users from leaderboard
      if (EXCLUDED_EMAILS.has(email.toLowerCase())) continue;

      const severity = extractSeverityFromStatus(risk.status);
      if (!severity || !isCountsSeverity(severity)) continue;

      const name = risk.name;
      if (!name) continue;

      // Deduplicate: only count each unique vulnerability name once per analyst
      // per severity, regardless of risk status (Open/Remediated/Accepted).
      const dedupeKey = `${severity}#${name}`;
      if (!seenNames.has(email)) {
        seenNames.set(email, new Set());
      }
      const seen = seenNames.get(email)!;
      if (seen.has(dedupeKey)) continue;
      seen.add(dedupeKey);

      if (!byEmail.has(email)) {
        byEmail.set(email, emptyCounts());
      }
      const counts = byEmail.get(email)!;
      counts[severity] += 1;
    }

    // Convert to sorted entries
    const unsorted: Omit<LeaderboardEntry, 'rank'>[] = [];
    for (const [email, counts] of byEmail) {
      unsorted.push({
        email,
        displayName: email.split('@')[0],
        score: computeScore(counts),
        counts,
      });
    }

    unsorted.sort((a, b) => b.score - a.score);

    return unsorted.map((entry, index) => ({
      ...entry,
      rank: index + 1,
    }));
  }, [query.data]);

  return {
    entries,
    status: query.status,
  };
}
